import React, { useState } from 'react';
// @mui
import { Stack, Box, IconButton, Tooltip } from '@mui/material';
import AddUserPopup from 'src/components/dialogPopUp/AddUserPopup';
import { useSelector } from 'react-redux';
import { useAuth } from 'src/hooks';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { dispatch } from 'src/redux/store';
import { fetchMyPermissions } from 'src/redux/slices/permissions';
import useFeaturePermission from 'src/hooks/useFeaturePermission';
// config
import { NAV } from '../../../config';
// utils
import { hideScrollbarX } from '../../../utils/cssStyles';
// components
import { Iconify } from '../../../components';
import { SvgColor } from '../../../components/svg-color';
import { NavSectionMini } from '../../../components/nav-section';
//
import getNavConfig from './NavConfig';
import NavToggleButton from './CollapseButton';
import { userIconWrapperStyle, userIconInnerStyle } from '../../../styles/NavBarVerticalStyle';

// Module-level sentinel — see NavbarVertical.jsx for the #80 rationale.
const EMPTY_HIDDEN_PARENT_TABLES = Object.freeze([]);

// ----------------------------------------------------------------------

export default function NavMini() {
    const [userOpen, setUserOpen] = useState(false);

    const { practiceUser: { results } } = useSelector(state => state.practiceUser);
    const practiceId = useSelector(state => state.practiceTreatmentList?.practiceId);
    const hasMultiplePractices = useSelector(state => (state.practiceTreatment?.results?.length || 0) > 1);
    const hasGroupMembership = useSelector(state => (state.groupDashboard?.groups?.length || 0) > 0);
    const hasTasksV2 = useSelector(state => {
        const perm = state.permissions?.permissions?.['tasks-v2'];
        return typeof perm === 'string' && perm !== 'hidden';
    });
    // See NavbarVertical.jsx — avoid returning a new `[]` each selector call.
    const hiddenParentTablesRaw = useSelector(state => state.practice?.practice?.preferences?.hidden_parent_tables);
    const hiddenParentTables = React.useMemo(() => hiddenParentTablesRaw || EMPTY_HIDDEN_PARENT_TABLES, [hiddenParentTablesRaw]);
    const { user } = useAuth();
    const practitionerDashboard = useFeaturePermission('practitioner-dashboard');
    const isPractitionerFlagUser = practitionerDashboard?.canView && !practitionerDashboard?.canManage;

    const getUserList = () => results.filter(el => el.user && el.user.id === user?.pk);
    const isPractitioner = getUserList()[0]?.role === "Practitioner"

    const [newNavConfig, setNewNavConfig] = React.useState([]);
    const [consentVersion] = React.useState('v1');

    React.useEffect(() => {
        if (practiceId) {
            dispatch(fetchMyPermissions(practiceId));
        }
    }, [practiceId]);

    React.useEffect(() => {
        const newNav = [];
        if (results?.length) {
            if (isPractitioner && isPractitionerFlagUser) {
                newNav.push({ subheader: 'dashboard', items: [{ title: 'Dashboard', path: PATH_DASHBOARD.general.app, icon: <SvgColor src={`/assets/icons/navbar/ic_dashboard.svg`} /> }] });
                setNewNavConfig(newNav);
            } else if ((!isPractitioner && !isPractitionerFlagUser ) || (isPractitioner &&isPractitionerFlagUser !== null)) {
                setNewNavConfig(getNavConfig(practiceId, { isSuperAdmin: getUserList()[0]?.user?.is_superuser, userRole: getUserList()[0]?.role, hasMultiplePractices, consentVersion, hasGroupMembership, hasTasksV2, hiddenParentTables }));
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPractitionerFlagUser, results, practiceId, hasMultiplePractices, consentVersion, hasGroupMembership, hasTasksV2, hiddenParentTables]);

    return (
        <Box
            component="nav"
            sx={{
                flexShrink: { lg: 0 },
                width: { lg: NAV.W_DASHBOARD_MINI },
                bgcolor: 'common.white',
            }}
        >
            <NavToggleButton
                sx={{
                    top: 22,
                    left: NAV.W_DASHBOARD_MINI - 12,
                }}
            />

            <Stack
                sx={{
                    pb: 2,
                    height: 1,
                    position: 'fixed',
                    width: NAV.W_DASHBOARD_MINI,
                    borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
                    ...hideScrollbarX,
                }}
            >

                <NavSectionMini data={newNavConfig} isPractitioner={isPractitionerFlagUser} />
                {!isPractitionerFlagUser && newNavConfig.length ? <Box sx={{ flexGrow: 1, ...userIconWrapperStyle }} >
                    <Box sx={{ ...userIconInnerStyle, justifyContent: 'center' }}>
                        <Tooltip title="Add User" arrow>
                            <IconButton onClick={() => setUserOpen(true)} sx={{ p: 0 }}>
                                <Iconify icon={'wpf:adduser'} width={18} height={18} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box> : null}
            </Stack>
            <AddUserPopup userOpen={userOpen} setUserOpen={setUserOpen} />
        </Box>
    );
}
